import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { FLOATING_ELEMENT_PLACEMENT } from '../../../../constants/common'
import { getQaSelector } from '../../../../utils/common'
import Button, { BUTTON_HEIGHT_32 } from '../../../Button'
import { FloatingTooltip } from '../../../Floating'
import { Status, STATUS_SIZE_16 } from '../../../Status'
import Text from '../../../Text'
import {
  PHASE_STRIP_AWAITING_APPROVAL_TEXT_COLOR,
  PHASE_STRIP_TITLE_KEY,
  QA_NAME_PHASE_STRIP_ACTION,
} from '../RentalPortalPhaseBar.constants'
import {
  checkIsPhaseStripActive,
  checkIsPhaseStripDone,
  checkIsPhaseUpcoming,
  getIsPhaseStripReadOnly,
  getPhaseStripAction,
  getPhaseStripActionItemIndex,
  getPhaseStripItems,
  getPhaseStripTitleKey,
} from '../RentalPortalPhaseBar.utils'

import { PhaseStripError } from './PhaseStripError'
import { PhaseStripItem } from './PhaseStripItem'
import { PhaseStripSkeleton } from './PhaseStripSkeleton'

import * as styles from './PhaseStrip.module.css'

function PhaseStrip({
  isAwaitingApproval,
  isLoading,
  isTerminal,
  phase,
  primaryAction,
  qaName,
  onKeyDown,
  onRetry,
}) {
  const { t } = useTranslation()

  const isReadOnly = getIsPhaseStripReadOnly({ isTerminal, phase })

  const items = getPhaseStripItems({ isActive: !isReadOnly, phase })

  const action = getPhaseStripAction({
    isTerminal,
    phase,
    primaryAction,
  })

  const actionItemIndex = action ? getPhaseStripActionItemIndex(items) : -1

  const actionButton = action ? (
    <Button.Outlined
      showTextTooltip
      className={styles.action}
      disabled={action.isDisabled}
      height={BUTTON_HEIGHT_32}
      qaName={QA_NAME_PHASE_STRIP_ACTION}
      text={t(action.label)}
      onClick={action.onClick}
    />
  ) : null

  const actionNode =
    action?.isDisabled && action?.tooltipKey ? (
      <FloatingTooltip
        content={t(action.tooltipKey)}
        placement={FLOATING_ELEMENT_PLACEMENT.top}
      >
        <span className={styles.actionTooltipWrapper}>{actionButton}</span>
      </FloatingTooltip>
    ) : (
      actionButton
    )

  const stripClassName = classNames(styles.PhaseStrip, {
    [styles.active]: checkIsPhaseStripActive({ isTerminal, phase }),
    [styles.done]: checkIsPhaseStripDone({ isTerminal, phase }),
  })

  return (
    <div
      {...getQaSelector({ value: qaName })}
      className={stripClassName}
      onKeyDown={onKeyDown}
    >
      {isLoading ? (
        <PhaseStripSkeleton />
      ) : phase?.strip ? (
        <>
          <Text className={styles.title}>
            {t(
              getPhaseStripTitleKey({
                phase,
                titleKeys: PHASE_STRIP_TITLE_KEY,
              })
            )}
          </Text>

          {isAwaitingApproval ? (
            <Status
              className={styles.badge}
              height={STATUS_SIZE_16}
              text={t('RentalPortal.PhaseStripAwaitingApprovalKey')}
              textColor={PHASE_STRIP_AWAITING_APPROVAL_TEXT_COLOR}
            />
          ) : null}

          {items.length > 0 ? (
            <div className={styles.items}>
              {items.map((item, index) => (
                <PhaseStripItem {...item} key={index}>
                  {index === actionItemIndex ? actionNode : null}
                </PhaseStripItem>
              ))}
            </div>
          ) : (
            <Text isSecondary className={styles.line}>
              {t('RentalPortal.PhaseStripEmptyKey')}
            </Text>
          )}

          {checkIsPhaseUpcoming(phase) ? (
            <Text isSecondary className={styles.line}>
              {t('RentalPortal.PhaseStripUpcomingReadOnlyKey')}
            </Text>
          ) : null}

          {action && actionItemIndex === -1 ? actionNode : null}
        </>
      ) : (
        <PhaseStripError onRetry={onRetry} />
      )}
    </div>
  )
}

export default PhaseStrip
